import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { SoloExpeditionRotationState } from '~/data/soloExpeditionRotation'

export interface PokemonSnapshot {
  id: number
  slug: string
  nameFr: string
  nameEn: string
  level: number
  stars: number
  rarity: string
  isShiny: boolean
}

export interface ActiveExpedition {
  id: string
  destinationId: string
  pokemonSnapshots: PokemonSnapshot[]
  teamDps: number
  startedAt: number
  endsAt: number
  claimed: boolean
}

export interface ExpeditionResult {
  caught: boolean
  legendarySlug?: string
  legendaryNameFr?: string
  legendaryNameEn?: string
  legendaryStars?: number
  legendaryIsShiny?: boolean
  goldEarned: number
  candyXlEarned: number
}

export const useExpeditionStore = defineStore('expedition', () => {
  const expeditions = ref<ActiveExpedition[]>([])
  const soloRotation = ref<SoloExpeditionRotationState | null>(null)

  function setFromLoad(data: unknown[]) {
    if (!Array.isArray(data)) {
      expeditions.value = []
      return
    }
    expeditions.value = data.map((raw: any) => ({
      id: raw.id ?? '',
      destinationId: raw.destinationId ?? '',
      pokemonSnapshots: Array.isArray(raw.pokemonSnapshots) ? raw.pokemonSnapshots : [],
      teamDps: raw.teamDps ?? 0,
      startedAt: raw.startedAt ?? 0,
      endsAt: raw.endsAt ?? 0,
      claimed: raw.claimed ?? false,
    }))
  }

  function addExpedition(expedition: ActiveExpedition) {
    expeditions.value.push(expedition)
  }

  function removeExpedition(id: string) {
    expeditions.value = expeditions.value.filter((e) => e.id !== id)
  }

  function setSoloRotation(rotation: SoloExpeditionRotationState | null | undefined) {
    soloRotation.value = rotation ?? null
  }

  /** Nombre d'expéditions non réclamées en cours ou terminées. */
  const activeCount = computed(() => expeditions.value.filter((e) => !e.claimed).length)

  /** Expéditions terminées mais non réclamées. */
  const readyToClaimCount = computed(() => {
    const nowSec = Math.floor(Date.now() / 1000)
    return expeditions.value.filter((e) => !e.claimed && e.endsAt <= nowSec).length
  })

  return {
    expeditions,
    soloRotation,
    activeCount,
    readyToClaimCount,
    setFromLoad,
    setSoloRotation,
    addExpedition,
    removeExpedition,
  }
})
